import { Blossom } from "@/components/brand/Brand";
import { Clock } from "lucide-react";
import { useLeadTime } from "@/lib/leadTime";
import { combineExtras, totalLeadTime } from "@/lib/patternAvailability";

export const ARTISAN_MESSAGE =
  "Por aqui, cada peça é feita à mão por mim, com tempo, cuidado e muito carinho 🤍 São só duas mãos a dar vida a todas as encomendas, por isso a tua pode precisar de algumas semanas até estar pronta para seguir viagem até ti. Obrigada por respeitares o tempo de criar algo especial!";

// Exceção de padrões "por encomenda": acrescenta-se ao prazo geral (o MAIOR acréscimo, ver combineExtras) e diz-se com todas as letras.
// `extra` vem de combineExtras (loja) ou da encomenda já feita (pattern_extra, com os mesmos campos).
export const PatternExtraLines = ({ lt, extra, className = "" }) => {
  if (!extra) return null;
  const names = extra.patterns.join(", ");
  const one = extra.patterns.length === 1;
  const total = totalLeadTime(lt, extra);
  return (
    <div className={`border-l-2 border-bordeaux/40 py-0.5 pl-3 ${className}`} data-testid="lead-time-pattern-extra">
      <p className="text-[14px] leading-relaxed text-ink">
        <strong className="font-semibold">{one ? `O padrão ${names} está` : `Os padrões ${names} estão`} por encomenda</strong>
        {extra.text ? <> e poderá acrescentar <strong className="font-semibold">{extra.text}</strong> ao prazo de preparação.</> : <> e o prazo poderá ser superior ao habitual. Confirmo contigo.</>}
      </p>
      {total && <p className="mt-1.5 text-[14px] leading-relaxed text-ink" data-testid="lead-time-total">Prazo estimado com {one ? "este padrão" : "estes padrões"}: <strong className="font-semibold">{total.text}</strong>.</p>}
      {extra.unknown && extra.text && <p className="mt-1.5 text-[13px] text-taupe-strong">Para algum dos padrões, confirmo o prazo contigo.</p>}
    </div>
  );
};

// Texto do prazo, partilhado pelas variantes. `lt` vem do painel (ou de uma encomenda já feita).
export const LeadTimeLines = ({ lt, extra = null, className = "" }) => (
  <div className={className}>
    <p className="text-sm leading-relaxed text-ink sm:text-[15px]" data-testid="lead-time-estimate">
      {lt?.text ? (
        <>O prazo estimado de preparação é atualmente de <strong className="font-semibold">{lt.text}</strong>, antes do envio.</>
      ) : (
        "Confirmo contigo o prazo de preparação, antes do envio."
      )}
    </p>
    {lt?.note && <p className="mt-1.5 text-sm italic leading-relaxed text-taupe-strong sm:text-[15px]" data-testid="lead-time-note">{lt.note}</p>}
    <PatternExtraLines lt={lt} extra={extra} className="mt-2.5" />
    <p className="mt-1.5 text-sm leading-relaxed text-taupe-strong" data-testid="lead-time-delivery">A este prazo acresce o tempo de entrega da transportadora.</p>
  </div>
);

// Selo curto para o topo da página de produto: quem faz e quanto demora, logo à vista.
export const LeadTimeChip = ({ className = "" }) => {
  const lt = useLeadTime();
  if (lt === null) return null;
  return (
    <div className={`flex items-start gap-2.5 rounded-2xl bg-rose-soft/80 px-3.5 py-2.5 text-[13px] leading-snug text-ink ${className}`} data-testid="lead-time-chip">
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-bordeaux" aria-hidden="true" />
      <div className="space-y-1.5">
        <p className="font-semibold">Feito por encomenda, não existem stock’s.</p>
        <p>Cada peça é feita do início ao fim para o teu bebé. Cada peça pode demorar algum tempo a ser criada. Faz o teu pedido com antecedência 💖</p>
      </div>
    </div>
  );
};

/**
 * variant="full": página de produto, com a mensagem da produção artesanal.
 * variant="short": carrinho e checkout, só a estimativa.
 */
export const LeadTimeNote = ({ variant = "short", className = "", patterns = [] }) => {
  const lt = useLeadTime();
  if (lt === null) return null;
  const extra = combineExtras(patterns);
  if (variant === "full") {
    return (
      <aside className={`overflow-hidden rounded-3xl bg-[linear-gradient(135deg,#F6E4E1,#FAF0ED)] p-3 ring-1 ring-bordeaux/10 sm:p-6 ${className}`} aria-label="Prazo de preparação" data-testid="lead-time-full">
        <div className="hidden items-center gap-3.5 sm:flex">
          <img src="/img/about/alexandra-400.webp" alt="" className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white" />
          <p className="font-display text-[1.125rem] leading-snug text-ink">Sou só eu a fazer cada peça <span aria-hidden="true">🤍</span><span className="mt-0.5 block font-[Pacifico] text-[15px] font-normal text-bordeaux">Alexandra</span></p>
        </div>
        <LeadTimeLines lt={lt} extra={extra} className="rounded-2xl bg-ivory/80 px-4 py-3 sm:mt-4" />
        <p className="mt-3 hidden text-sm leading-relaxed text-taupe-strong sm:block" data-testid="lead-time-artisan">{ARTISAN_MESSAGE}</p>
      </aside>
    );
  }
  return (
    <aside className={`rounded-lg bg-rose-soft/70 px-4 py-3.5 sm:px-5 sm:py-4 ${className}`} aria-label="Prazo de preparação" data-testid={`lead-time-${variant}`}>
      <p className="flex items-center gap-2 font-display text-base leading-snug text-ink sm:text-[1.0625rem]">
        <Blossom className="h-4 w-4 shrink-0 text-rose" /> Feito com carinho, só para ti 🤍
      </p>
      <LeadTimeLines lt={lt} extra={extra} className="mt-2" />
    </aside>
  );
};
